import React from 'react';
import { useAuth } from '../services/auth.service';
import { Box, Button, Typography, keyframes } from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { styled } from '@mui/material/styles';

// Keyframes for entrance and pulsing animation
const slideDown = keyframes`
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 94, 98, 0.4);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(255, 94, 98, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 94, 98, 0);
  }
`;

// Styled container for premium glassmorphism & gradients
const BannerContainer = styled(Box)(({ theme }) => ({
  position: 'sticky',
  top: 0,
  zIndex: 9999,
  width: '100%',
  background: 'linear-gradient(90deg, #111b21 0%, #1e293b 50%, #111b21 100%)',
  borderBottom: '2px solid #ff5e62',
  color: '#ffffff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '12px 24px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
  animation: `${slideDown} 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards`,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: '12px',
    textAlign: 'center',
    padding: '12px 16px',
  },
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: '6px',
  },
}));

const GlowIcon = styled(SwapHorizIcon)(({ theme }) => ({
  color: '#ff5e62',
  fontSize: '24px',
  filter: 'drop-shadow(0 0 6px rgba(255, 94, 98, 0.8))',
}));

const ImpersonateText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Outfit, Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  letterSpacing: '0.3px',
  '& strong': {
    color: '#ff5e62',
    fontWeight: 700,
    textDecoration: 'underline',
  },
}));

const ReturnButton = styled(Button)(({ theme }) => ({
  marginLeft: '24px',
  background: 'linear-gradient(135deg, #ff5e62 0%, #ff9966 100%)',
  color: '#ffffff',
  textTransform: 'none',
  fontFamily: 'Outfit, Inter, sans-serif',
  fontWeight: 600,
  fontSize: '13px',
  padding: '6px 18px',
  borderRadius: '20px',
  boxShadow: '0 4px 12px rgba(255, 94, 98, 0.3)',
  border: 'none',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  animation: `${pulse} 2s infinite`,
  '&:hover': {
    background: 'linear-gradient(135deg, #ff4045 0%, #ff803b 100%)',
    transform: 'scale(1.05)',
    boxShadow: '0 6px 16px rgba(255, 94, 98, 0.5)',
  },
  '&:active': {
    transform: 'scale(0.98)',
  },
  [theme.breakpoints.down('sm')]: {
    marginLeft: 0,
    width: '100%',
  },
}));

export const ImpersonationBanner = () => {
  const { isImpersonating, stopImpersonating, user } = useAuth();

  if (!isImpersonating || !isImpersonating()) {
    return null;
  }

  return (
    <BannerContainer>
      <ContentWrapper>
        <GlowIcon />
        <ImpersonateText>
          Sesión de administrador activa: Impersonando a <strong>{user?.username || user?.email || 'Usuario'}</strong> (Modo Vista)
        </ImpersonateText>
      </ContentWrapper>
      <ReturnButton onClick={stopImpersonating}>
        Volver a Administrador
      </ReturnButton>
    </BannerContainer>
  );
};

export default ImpersonationBanner;
